import { Flex, Text } from '@mantine/core';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TargetComparison } from '../../../../../../api/targets-client/targets-client.type';
import { formatDate } from '../../../../../../helpers/date-time-helpers';
import { useTargetsTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { skyLight } from '../../../../../../styles/design-tokens';
import { getTargetStrokeDashArray } from '../../../../../adherence/targets/targets.helpers';
import { getMeasureDataTypes, getMeasureDataTypesColors } from '../../targets-tab.helpers';
import { useTargetChartData } from '../../targets-tab.hooks';
import { MeasureDataType } from '../../targets-tab.type';
import { TargetsChartLegend } from './targets-chart-legend';
import { TargetsChartTooltip } from './targets-chart-tooltip';

export const TargetsChart = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation();
  const { chartData } = useTargetChartData();
  const visibleMeasures = useTargetsTabStore((state) => state.visibleMeasures);
  const selectedTarget = useTargetsTabStore((state) => state.selectedTarget);

  const measureDataTypes = getMeasureDataTypes();
  const measureDataTypesColors = getMeasureDataTypesColors();

  if (!chartData || chartData.length === 0) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Text size="medium">{t('common.labels.no_data')}</Text>
      </Flex>
    );
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <AreaChart data={chartData} margin={{ top: 20 }}>
          <defs>
            {measureDataTypes.map((measureDataType) => (
              <linearGradient
                key={`gradient-${measureDataType}`}
                id={`color-${measureDataType}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="20%" stopColor={measureDataTypesColors[measureDataType]} stopOpacity={0.6} />
                <stop offset="80%" stopColor={measureDataTypesColors[measureDataType]} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(date: string) => formatDate(date)}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: t('strategy.initiatives.metrics.units'),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />

          <Tooltip content={<TargetsChartTooltip />} />

          {visibleMeasures[MeasureDataType.Actual] && (
            <Area
              key={MeasureDataType.Actual}
              type="monotone"
              dataKey={MeasureDataType.Actual as string}
              name={MeasureDataType.Actual}
              stroke={measureDataTypesColors[MeasureDataType.Actual]}
              fill={`url(#color-${MeasureDataType.Actual})`}
              fillOpacity={0.6}
              strokeOpacity={1}
              strokeWidth={1}
              connectNulls={false}
              animationDuration={0}
            />
          )}

          {visibleMeasures[MeasureDataType.Target] &&
            selectedTarget &&
            ([TargetComparison.RANGE_IN, TargetComparison.RANGE_EX].includes(selectedTarget.target_comparison) ? (
              <Area
                key={MeasureDataType.Target}
                type="monotone"
                dataKey={MeasureDataType.Target as string}
                name={MeasureDataType.Target}
                stroke={measureDataTypesColors[MeasureDataType.Target]}
                fill={`url(#color-${MeasureDataType.Target})`}
                fillOpacity={0.6}
                strokeOpacity={1}
                strokeWidth={1}
                strokeDasharray={getTargetStrokeDashArray(selectedTarget.target_comparison)}
                connectNulls={false}
                animationDuration={0}
              />
            ) : (
              <Area
                key={MeasureDataType.Target}
                type="monotone"
                dataKey={MeasureDataType.Target as string}
                name={MeasureDataType.Target}
                stroke={measureDataTypesColors[MeasureDataType.Target]}
                fill={
                  selectedTarget.target_comparison === TargetComparison.EQ
                    ? 'transparent'
                    : `url(#color-${MeasureDataType.Target})`
                }
                fillOpacity={0.6}
                strokeOpacity={1}
                strokeWidth={1}
                strokeDasharray={getTargetStrokeDashArray(selectedTarget.target_comparison)}
                connectNulls={false}
                animationDuration={0}
                baseValue={
                  selectedTarget.target_comparison === TargetComparison.GT ||
                  selectedTarget.target_comparison === TargetComparison.GTE
                    ? 'dataMax'
                    : 'dataMin'
                }
              />
            ))}

          <Legend content={<TargetsChartLegend />} verticalAlign="bottom" height={36} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

TargetsChart.displayName = 'TargetsChart';
